import { useMemo } from 'react';

import { type MyProfileRes, useGetMyProfile } from '@/api';
import { type UserType } from '@/types';

import { type ProfileEditFormData } from '../schema';

// 프로필 수정 폼 기본값 설정
export const useFormDefaultValues = () => {
  const { data: userProfile } = useGetMyProfile();
  return useMemo(() => {
    if (!userProfile) {
      return undefined;
    }

    const userType = userProfile.data.type.toLowerCase() as UserType;

    const baseValues: Partial<ProfileEditFormData> = {
      userName: userProfile.data.username || '',
      email: userProfile.data.email || '',
      userType: userType,
      countryCode: userProfile.data.countryCode || '',
      emailMarketingAgreement:
        userProfile.data.marketingConsent.emailConsent || false,
      phoneMarketingAgreement:
        userProfile.data.marketingConsent.phoneConsent || false,
      phoneNumber: userProfile.data.phoneNumber || '',
    };

    return getDefaultValuesByUserType(userProfile.data, baseValues);
  }, [userProfile]);
};

const getDefaultValuesByUserType = (
  userProfile: MyProfileRes,
  baseValues: Partial<ProfileEditFormData>,
): Partial<ProfileEditFormData> => {
  switch (userProfile.type.toLowerCase()) {
    case 'student':
      return {
        ...baseValues,
        schoolName: userProfile.memberInfo?.studentInfo?.institution || '',
        schoolType: userProfile.memberInfo?.studentInfo?.schoolType || '',
        schoolTypeCode:
          userProfile.memberInfo?.studentInfo?.schoolTypeCode || '',
        schoolGrade: userProfile.memberInfo?.studentInfo?.schoolGrade,
        schoolGradeCode:
          userProfile.memberInfo?.studentInfo?.schoolGradeCode || '',
        verifiedSchool: userProfile.memberInfo?.studentInfo?.verifiedSchool
          ? {
              standardCode:
                userProfile.memberInfo.studentInfo.verifiedSchool.standardCode,
            }
          : null,
      };

    case 'teacher':
      return {
        ...baseValues,
        schoolName: userProfile.memberInfo?.teacherInfo?.institution || '',
        schoolType: userProfile.memberInfo?.teacherInfo?.schoolType || '',
        schoolTypeCode:
          userProfile.memberInfo?.teacherInfo?.schoolTypeCode || '',
        schoolGrade: userProfile.memberInfo?.teacherInfo?.schoolGrade,
        schoolGradeCode:
          userProfile.memberInfo?.teacherInfo?.schoolGradeCode || '',
        verifiedSchool: userProfile.memberInfo?.teacherInfo?.verifiedSchool
          ? {
              standardCode:
                userProfile.memberInfo.teacherInfo.verifiedSchool.standardCode,
            }
          : null,
        verifiedPhoneNumber: userProfile.verifiedPhoneNumber || '',
      };

    case 'company':
      return {
        ...baseValues,
        companyName: userProfile.memberInfo?.companyInfo?.name || '',
        position: userProfile.memberInfo?.companyInfo?.position || '',
        verifiedPhoneNumber: userProfile.verifiedPhoneNumber || '',
      };

    default:
      return {
        ...baseValues,
        verifiedPhoneNumber: userProfile.verifiedPhoneNumber || '',
      };
  }
};
